// src/pages/AdminSourceReport.tsx
import React, { useState, useEffect, useCallback } from 'react'
import { apiService } from '../services/api'
import { Loader2, RefreshCw } from 'lucide-react'

type Preset = 'today' | 'week' | 'month' | 'all'
interface SourceRow { source: string; count: number }
interface SourceReport { total: number; sources: SourceRow[] }

const toYmd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

function range(p: Preset): { s?: string; e?: string } {
  const now = new Date(); const today = toYmd(now)
  if (p === 'today') return { s: today, e: today }
  if (p === 'week') { const d = new Date(now); d.setDate(d.getDate() - 6); return { s: toYmd(d), e: today } }
  if (p === 'month') { const d = new Date(now.getFullYear(), now.getMonth(), 1); return { s: toYmd(d), e: today } }
  return { s: '2020-01-01', e: today } // tüm zamanlar
}

export default function AdminSourceReport() {
  const [preset, setPreset] = useState<Preset>('all')
  const [data, setData] = useState<SourceReport | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async (p: Preset) => {
    setLoading(true)
    try {
      const r = range(p)
      const res = await apiService.getAdminSourceReport(r.s, r.e)
      // "(doğrudan)" (kaynaksız/normal kayıtlar) gösterilmez — sadece reklam kaynakları
      const sources: SourceRow[] = (res?.sources || []).filter((s: SourceRow) => s.source !== '(doğrudan)')
      setData({ total: sources.reduce((a, s) => a + s.count, 0), sources })
    }
    catch { setData(null) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load(preset) }, [preset, load])

  const presets: { key: Preset; label: string }[] = [
    { key: 'today', label: 'Bugün' },
    { key: 'week', label: 'Bu Hafta' },
    { key: 'month', label: 'Bu Ay' },
    { key: 'all', label: 'Tüm Zamanlar' },
  ]

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap items-center gap-2">
        {presets.map(p => (
          <button key={p.key} onClick={() => setPreset(p.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${preset === p.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            {p.label}
          </button>
        ))}
        <button onClick={() => load(preset)} className="ml-auto p-2 text-gray-500 hover:text-blue-600" title="Yenile">
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {data && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-sm text-gray-500">Reklamdan Gelen Kayıt</p>
          <p className="text-2xl font-bold text-gray-800">{data.total}</p>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
        ) : !data || data.sources.length === 0 ? (
          <p className="text-center text-gray-500 py-8 text-sm">Bu aralıkta kayıt yok.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Kaynak</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-700">Kayıt</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-700">Oran</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.sources.map((s, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">{s.source}</td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-800">{s.count}</td>
                  <td className="px-4 py-3 text-right text-gray-500">{data.total > 0 ? Math.round((s.count / data.total) * 100) : 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
